import { type FormEvent, useEffect, useRef } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/policies-references.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import { POLICY_REFERENCE_ITEMS } from '#app/routes/mocra+/_form+/intake-options.ts'
import { PolicyDocumentsSection } from '#app/routes/mocra+/_form+/policies-references/policy-documents-section.tsx'
import { createSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'
import { type SectionKey } from '#app/routes/mocra+/_sections+/section-data.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Policies & References' },
]

const SECTION_KEY = 'policiesReferences'
const STEP_ID = 'policies-references'

type PolicyReferenceType = (typeof POLICY_REFERENCE_ITEMS)[number]['value']
type PolicyCategory = (typeof POLICY_REFERENCE_ITEMS)[number]['category']

const CATEGORY_BADGE_STYLES: Record<PolicyCategory, string> = {
	policy: 'border-transparent bg-sky-100 text-sky-800 hover:bg-sky-200',
	procedure: 'border-transparent bg-teal-100 text-teal-800 hover:bg-teal-200',
	reference:
		'border-transparent bg-slate-100 text-slate-800 hover:bg-slate-200',
	plan: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-200',
	dataset:
		'border-transparent bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
	template:
		'border-transparent bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
	manual:
		'border-transparent bg-violet-100 text-violet-800 hover:bg-violet-200',
	sop: 'border-transparent bg-rose-100 text-rose-800 hover:bg-rose-200',
}

const CATEGORY_DESCRIPTIONS: Record<PolicyCategory, string> = {
	policy:
		'Policies set the governance expectations auditors will cite throughout inspections.',
	procedure:
		'Procedures document the repeatable steps teams follow to stay compliant.',
	reference:
		'Reference materials support artwork, brand standards, or supporting guidance.',
	plan: 'Plans describe crisis or communication playbooks activated during events.',
	dataset:
		'Datasets point to the systems and reports regulators can request on demand.',
	template:
		'Templates provide reusable agreement or form starter files for your teams.',
	manual:
		'Manuals summarize quality system structure for auditors and leadership.',
	sop: 'SOP bundles showcase day-to-day operational controls inspectors will examine.',
}

function formatCategoryLabel(category: PolicyCategory) {
	return category
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase())
}

const policyReferenceLookup = new Map(
	POLICY_REFERENCE_ITEMS.map((item) => [item.value, item]),
)

const requiredPolicyTypes = new Set(
	POLICY_REFERENCE_ITEMS.filter((item) => item.required).map(
		(item) => item.value,
	),
)

const POLICY_TYPE_SECTIONS: Record<
	PolicyReferenceType,
	ReadonlyArray<SectionKey>
> = {
	complaint_handling_policy: ['605'],
	complaint_data_privacy_policy: ['605'],
	supplier_qualification_policy: ['608', 'overview'],
	document_control_policy: ['606', 'overview'],
	change_control_policy: ['606', 'overview', '612'],
	change_control_procedure: ['overview', '612'],
	document_control_procedure: ['606', 'overview'],
	brand_guidelines: ['609', '612'],
	artwork_approval_workflow: ['609', '612'],
	crisis_communication_plan: ['611'],
	distribution_records_dataset: ['610'],
	quality_agreement_template: ['608', 'overview'],
	risk_management_policy: ['608', 'overview', '612'],
	risk_management_program: ['608'],
	quality_manual: ['606'],
	sanitation_sop_bundle: ['606'],
	equipment_maintenance_sop: ['606'],
	deviation_capa_procedure: ['606'],
	hr_training_policy: ['606', 'overview'],
	other_reference: ['overview'],
}

function getPolicyResources(type: PolicyReferenceType) {
	const mocraSections = POLICY_TYPE_SECTIONS[type] ?? ['overview']
	return createSectionResources({
		mocraSections,
		templateSections: mocraSections,
	})
}

const DocumentReferenceBaseSchema = z
	.object({
		type: z.enum(
			POLICY_REFERENCE_ITEMS.map((item) => item.value) as [
				PolicyReferenceType,
				...PolicyReferenceType[],
			],
		),
		isComplete: z.enum(['true', 'false']).optional(),
		hasDraft: z.enum(['true', 'false']).optional(),
		needsHelp: z.enum(['true', 'false']).optional(),
		location: z
			.string()
			.max(200, 'Keep the location or link under 200 characters.')
			.optional(),
	})
	.superRefine((value, ctx) => {
		const location = value.location?.trim()
		const isComplete = value.isComplete
		const hasDraft = value.hasDraft
		const needsHelp = value.needsHelp

		if (!isComplete) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Select whether this policy is completed.',
				path: ['isComplete'],
			})
			return
		}

		if (isComplete === 'true') {
			if (!location) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Share where the completed policy lives.',
					path: ['location'],
				})
			}
			return
		}

		if (!hasDraft) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Let us know if you already have a draft.',
				path: ['hasDraft'],
			})
			return
		}

		if (hasDraft === 'true') {
			if (!location) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Link to the draft so reviewers can find it.',
					path: ['location'],
				})
			}
			return
		}

		if (!needsHelp) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Tell us whether you need help drafting this policy.',
				path: ['needsHelp'],
			})
		}
	})

const DocumentReferenceSchema = z.preprocess((input) => {
	if (!input || typeof input !== 'object') return input
	const record = { ...(input as Record<string, unknown>) }
	const convert = (value: unknown) => {
		if (value === true) return 'true'
		if (value === false) return 'false'
		if (typeof value === 'string') {
			if (value === 'true' || value === 'false') return value
		}
		return value
	}
	record.isComplete = convert(record.isComplete)
	record.hasDraft = convert(record.hasDraft)
	record.needsHelp = convert(record.needsHelp)
	if (typeof record.location === 'string') {
		record.location = record.location.trim()
	}
	if ('status' in record && typeof record.status === 'string') {
		const status = record.status
		record.isComplete = status === 'in_place' ? 'true' : 'false'
		record.hasDraft =
			status === 'in_development' ? 'true' : (record.hasDraft ?? undefined)
		record.needsHelp =
			status === 'needs_template' ? 'true' : (record.needsHelp ?? undefined)
		delete record.status
	}
	return record
}, DocumentReferenceBaseSchema)

const PoliciesReferencesSchema = z
	.object({
		documents: z
			.array(DocumentReferenceSchema, {
				required_error: 'Add the key policies, SOPs, and references.',
			})
			.min(
				POLICY_REFERENCE_ITEMS.filter(
					(item) => item.value !== 'other_reference',
				).length,
				'Capture each required policy, SOP, and reference.',
			),
	})
	.superRefine((value, ctx) => {
		const seen = new Map<PolicyReferenceType, number>()
		value.documents.forEach((doc, index) => {
			const previousIndex = seen.get(doc.type)
			if (previousIndex !== undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Each document type should appear only once.',
					path: ['documents', index, 'type'],
				})
			} else {
				seen.set(doc.type, index)
			}
		})

		for (const required of requiredPolicyTypes) {
			if (!seen.has(required)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Complete all required policy and SOP references.',
					path: ['documents'],
				})
				break
			}
		}
	})

export type PoliciesReferencesFormValues = z.infer<
	typeof PoliciesReferencesSchema
>

type DocumentReferenceEntry = z.infer<typeof DocumentReferenceSchema>

const BASE_POLICY_TYPES: PolicyReferenceType[] = POLICY_REFERENCE_ITEMS.filter(
	(item) => item.value !== 'other_reference',
).map((item) => item.value as PolicyReferenceType)

const DEFAULT_VALUES: PoliciesReferencesFormValues = {
	documents: BASE_POLICY_TYPES.map((type) => createDocumentReference(type)),
}

function createDocumentReference(
	type: PolicyReferenceType,
): DocumentReferenceEntry {
	return {
		type,
		isComplete: 'false',
		hasDraft: 'false',
		needsHelp: 'false',
		location: '',
	}
}

export default function PoliciesReferences() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = useSectionForm({
		section: SECTION_KEY,
		schema: PoliciesReferencesSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizePoliciesReferencesDraft(values)
		if (normalized && !policiesReferencesValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (!normalized && !policiesReferencesValuesEqual(DEFAULT_VALUES, values)) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const documents = values.documents
	const documentFieldErrors = getFieldErrors('documents')

	const documentItems = documents.map((document, index) => {
		const docPath = `documents.${index}`
		const currentType = document.type
		const meta = policyReferenceLookup.get(currentType)
		const category = meta?.category ?? 'reference'
		const badgeClassName = CATEGORY_BADGE_STYLES[category]
		const categoryDescription = CATEGORY_DESCRIPTIONS[category]
		const isRequired = meta?.required ?? false
		const documentLabel = meta?.label ?? `Document ${index + 1}`
		const resources = getPolicyResources(currentType)
		const categoryLabel = formatCategoryLabel(category)
		const categoryLabelLower = categoryLabel.toLowerCase()

		const isCompleteValue = document.isComplete
		const hasDraftValue = document.hasDraft
		const showDraftQuestion = isCompleteValue === 'false'
		const showHelpQuestion = showDraftQuestion && hasDraftValue === 'false'
		const showLocationField =
			isCompleteValue === 'true' || hasDraftValue === 'true'

		return {
			key: `${document.type}-${index}`,
			resources,
			documentLabel,
			description: categoryDescription,
			categoryBadgeLabel: categoryLabel,
			categoryBadgeClassName: badgeClassName,
			isRequired,
			isCompleteName: `${docPath}.isComplete`,
			hasDraftName: `${docPath}.hasDraft`,
			needsHelpName: `${docPath}.needsHelp`,
			locationName: `${docPath}.location`,
			isCompleteValue,
			hasDraftValue,
			needsHelpValue: document.needsHelp,
			locationValue: document.location ?? '',
			categoryLabelLower,
			showDraftQuestion,
			showHelpQuestion,
			showLocationField,
			isCompleteErrors: getFieldErrors(`${docPath}.isComplete`),
			hasDraftErrors: getFieldErrors(`${docPath}.hasDraft`),
			needsHelpErrors: getFieldErrors(`${docPath}.needsHelp`),
			locationErrors: getFieldErrors(`${docPath}.location`),
			onCompleteChange: (value: 'true' | 'false') => {
				setFieldValue(`${docPath}.isComplete`, value)
				setFieldValue(`${docPath}.hasDraft`, undefined)
				setFieldValue(`${docPath}.needsHelp`, undefined)
			},
			onHasDraftChange: (value: 'true' | 'false') => {
				setFieldValue(`${docPath}.hasDraft`, value)
				setFieldValue(`${docPath}.needsHelp`, undefined)
			},
			onNeedsHelpChange: (value: 'true' | 'false') => {
				setFieldValue(`${docPath}.needsHelp`, value)
			},
			onLocationChange: (value: string) => {
				setFieldValue(`${docPath}.location`, value)
			},
			onRemove: isRequired
				? undefined
				: () =>
						setFieldValue(
							'documents',
							documents.filter((_, docIndex) => docIndex !== index),
						),
			removeAriaLabel: isRequired
				? undefined
				: `Remove ${documentLabel.toLowerCase()}`,
		}
	})

	return (
		<Page
			title="Policies & References"
			description="Link the policies, SOPs, and source documents that underpin your MoCRA program."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event: FormEvent<HTMLFormElement>) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />
				<PolicyDocumentsSection
					items={documentItems}
					documentsError={documentFieldErrors[0] ?? null}
				/>
				<Divider />

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

export function normalizePoliciesReferencesDraft(
	draft: unknown,
): PoliciesReferencesFormValues | null {
	if (!draft || typeof draft !== 'object') return null
	const record = draft as { documents?: unknown }

	const normalized: DocumentReferenceEntry[] = []
	const seenTypes = new Set<PolicyReferenceType>()

	if (Array.isArray(record.documents)) {
		for (const entry of record.documents) {
			const normalizedEntry = normalizeDocumentReference(entry)
			if (!normalizedEntry) continue
			if (seenTypes.has(normalizedEntry.type)) continue
			normalized.push(normalizedEntry)
			seenTypes.add(normalizedEntry.type)
		}
	}

	for (const baseType of BASE_POLICY_TYPES) {
		if (!seenTypes.has(baseType)) {
			normalized.push(createDocumentReference(baseType))
			seenTypes.add(baseType)
		}
	}

	return {
		documents: normalized,
	}
}

export function normalizeDocumentReference(
	entry: unknown,
): DocumentReferenceEntry | null {
	if (!entry || typeof entry !== 'object') return null
	const record = { ...(entry as Record<string, unknown>) }

	const rawType = typeof record.type === 'string' ? record.type : null
	const type =
		rawType && policyReferenceLookup.has(rawType as PolicyReferenceType)
			? (rawType as PolicyReferenceType)
			: null
	if (!type) return null

	const coerceBoolean = (value: unknown): 'true' | 'false' | undefined => {
		if (value === true || value === 'true') return 'true'
		if (value === false || value === 'false') return 'false'
		return undefined
	}

	if (typeof record.status === 'string') {
		const status = record.status
		record.isComplete = status === 'in_place' ? 'true' : 'false'
		record.hasDraft =
			status === 'in_development' ? 'true' : (record.hasDraft ?? undefined)
		record.needsHelp =
			status === 'needs_template' ? 'true' : (record.needsHelp ?? undefined)
	}

	let isComplete = coerceBoolean(record.isComplete)
	let hasDraft = coerceBoolean(record.hasDraft)
	let needsHelp = coerceBoolean(record.needsHelp)
	let location =
		typeof record.location === 'string' ? record.location.trim() : ''

	if (!isComplete) {
		isComplete = 'false'
	}

	if (isComplete === 'true' && !location) {
		isComplete = 'false'
	}

	if (isComplete === 'true') {
		return {
			type,
			isComplete: 'true',
			hasDraft: 'false',
			needsHelp: 'false',
			location,
		}
	}

	if (!hasDraft) {
		hasDraft = 'false'
	}

	if (hasDraft === 'true' && !location) {
		hasDraft = 'false'
	}

	if (hasDraft === 'true') {
		needsHelp = 'false'
	} else {
		needsHelp = needsHelp ?? 'false'
	}

	if (hasDraft === 'false') {
		location = ''
	} else if (!location) {
		location = ''
	}

	return {
		type,
		isComplete: 'false',
		hasDraft,
		needsHelp: needsHelp ?? 'false',
		location,
	}
}

export function policiesReferencesValuesEqual(
	left: PoliciesReferencesFormValues,
	right: PoliciesReferencesFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}
